import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, DiscountUpdateEvent } from '@theme/events';
import {
  fetchConfig,
  isMobileBreakpoint,
} from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';

/**
 * Awadh Trays cart drawer.
 *
 * Keeps the native Horizon cart behaviour intact while adding:
 * - automatic WELCOME10 application after a successful add-to-cart
 * - reliable retry handling while the cart drawer is being morphed
 * - direct Horizon-compatible cart discount application
 * - a clean hand-off to the existing Fastrr / Shiprocket headless checkout
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number | null} */
  #welcome10RetryTimer = null;

  /** @type {number | null} */
  #welcome10DeadlineTimer = null;

  /** @type {boolean} */
  #welcome10Applying = false;

  /** @type {boolean} */
  #welcome10Done = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    this.#historyAbortController?.abort();
    this.#clearWelcome10Timers();
  }

  #clearWelcome10Timers() {
    if (this.#welcome10RetryTimer) {
      window.clearInterval(this.#welcome10RetryTimer);
      this.#welcome10RetryTimer = null;
    }

    if (this.#welcome10DeadlineTimer) {
      window.clearTimeout(this.#welcome10DeadlineTimer);
      this.#welcome10DeadlineTimer = null;
    }
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController?.abort();
    this.#historyAbortController = new AbortController();

    window.addEventListener('popstate', this.#handlePopState, {
      signal: this.#historyAbortController.signal,
    });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();

    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty(
        '--dialog-drawer-closing-animation',
        'none'
      );

      await this.closeDialog();

      this.refs.dialog.style.removeProperty(
        '--dialog-drawer-closing-animation'
      );
    }
  };

  /**
   * Handles successful cart additions.
   *
   * Opens the drawer and starts a resilient WELCOME10 application window.
   *
   * @param {CustomEvent<{
   *   resource?: { item_count?: number },
   *   didError?: boolean
   * }>} event
   */
  #handleCartAdd = (event) => {
    if (event.detail?.didError) {
      this.#announceCartCount(event.detail.resource?.item_count);
      return;
    }

    this.#welcome10Done = false;
    this.#clearWelcome10Timers();

    /*
     * IMPORTANT:
     * Clear the old temporary block from earlier failed attempts.
     * We do not want one previous mobile failure to permanently
     * prevent WELCOME10 from being retried.
     */
    try {
      sessionStorage.removeItem('awadh_welcome10_autoapply_blocked');
    } catch (error) {
      console.warn(
        'Awadh: could not clear WELCOME10 session flag.',
        error
      );
    }

    // Preserve existing behaviour.
    this.showDialog();
    this.#announceCartCount(event.detail.resource?.item_count);

    /*
     * Wait briefly for the cart section to finish its initial morph,
     * then begin the retry window.
     */
    window.setTimeout(() => {
      this.#startWelcome10AutoApply();
    }, 150);
  };

  /**
   * Announces cart count to screen readers.
   *
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (
      this.refs.liveRegion
    );

    if (
      !this.refs.dialog?.open ||
      !liveRegion ||
      cartCount === undefined
    ) {
      return;
    }

    liveRegion.textContent =
      `${Theme.translations.cart_count}: ${cartCount}`;
  }

  /**
   * Starts the WELCOME10 retry window.
   *
   * The cart drawer is dynamically morphed after add-to-cart.
   * On mobile the cart-discount-component may appear later,
   * so we keep checking the live drawer DOM.
   */
  #startWelcome10AutoApply = () => {
    if (!this.refs.dialog?.open || this.#welcome10Done) return;

    const attempt = async () => {
      if (
        !this.refs.dialog?.open ||
        this.#welcome10Done
      ) {
        this.#clearWelcome10Timers();
        return;
      }

      const result = await this.#applyWelcome10();

      if (
        result === 'applied' ||
        result === 'already-applied'
      ) {
        this.#welcome10Done = true;
        this.#clearWelcome10Timers();
        return;
      }

      if (result === 'rejected') {
        this.#welcome10Done = true;
        this.#clearWelcome10Timers();
      }
    };

    /*
     * First attempt immediately.
     */
    void attempt();

    /*
     * Continue checking until the live cart section is ready.
     */
    this.#welcome10RetryTimer = window.setInterval(() => {
      void attempt();
    }, 300);

    /*
     * Maximum retry window.
     */
    this.#welcome10DeadlineTimer = window.setTimeout(() => {
      this.#clearWelcome10Timers();
    }, 10000);
  };

  /**
   * Applies WELCOME10 directly through the Horizon-compatible
   * Shopify Cart AJAX API.
   *
   * This mirrors the native Horizon cart-discount behaviour:
   * 1. Read existing discount pills.
   * 2. POST the discount to /cart/update.js.
   * 3. Request the cart section HTML.
   * 4. Check whether WELCOME10 is applicable.
   * 5. Morph the live cart section.
   *
   * @returns {Promise<
   *   'applied' |
   *   'already-applied' |
   *   'pending' |
   *   'rejected'
   * >}
   */
  #applyWelcome10 = async () => {
    const dialog = this.refs.dialog;

    if (!dialog?.open) {
      return 'pending';
    }

    /*
     * Prevent overlapping requests.
     */
    if (this.#welcome10Applying) {
      return 'pending';
    }

    const discountComponent = dialog.querySelector(
      'cart-discount-component'
    );

    if (!discountComponent) {
      return 'pending';
    }

    const form = discountComponent.querySelector(
      'form.cart-discount__form'
    );

    const input = discountComponent.querySelector(
      'input[name="discount"]'
    );

    const sectionId =
      discountComponent.dataset.sectionId;

    if (!form || !input || !sectionId) {
      return 'pending';
    }

    /*
     * Check whether WELCOME10 is already visible.
     */
    const existingPills = Array.from(
      discountComponent.querySelectorAll(
        '.cart-discount__pill'
      )
    );

    const existingDiscounts = existingPills
      .map((pill) =>
        pill
          .getAttribute('data-discount-code')
          ?.trim()
          .toUpperCase()
      )
      .filter(Boolean);

    const alreadyApplied = existingDiscounts.includes(
      'WELCOME10'
    );

    if (alreadyApplied) {
      discountComponent.setAttribute(
        'data-awadh-welcome10-applied',
        'true'
      );

      this.#dispatchWelcome10Applied();

      return 'already-applied';
    }

    /*
     * Prevent duplicate WELCOME10 requests within the same
     * retry window.
     */
    this.#welcome10Applying = true;

    input.value = 'WELCOME10';

    const abortController = new AbortController();

    try {
      /*
       * Build the same request structure used by
       * Horizon's native CartDiscount component.
       */
      const discountList = [
        ...existingDiscounts,
        'WELCOME10',
      ];

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: discountList.join(','),
          sections: [sectionId],
        }),
      });

      const response = await fetch(
        Theme.routes.cart_update_url,
        {
          ...config,
          signal: abortController.signal,
        }
      );

      if (!response.ok) {
        console.error(
          'Awadh: WELCOME10 request failed with HTTP status:',
          response.status
        );

        return 'pending';
      }

      const data = await response.json();

      /*
       * Shopify tells us whether the submitted code is
       * actually applicable to the current cart.
       */
      const discountResult =
        Array.isArray(data.discount_codes)
          ? data.discount_codes.find(
              (discount) =>
                discount.code?.trim().toUpperCase() ===
                'WELCOME10'
            )
          : null;

      if (
        discountResult &&
        discountResult.applicable === false
      ) {
        console.warn(
          'Awadh: WELCOME10 was returned as not applicable.'
        );

        return 'rejected';
      }

      /*
       * The section HTML returned by Shopify is the
       * authoritative new cart state.
       */
      const newHtml =
        data.sections?.[sectionId];

      if (!newHtml) {
        console.error(
          'Awadh: cart update succeeded but the cart section HTML was missing.'
        );

        return 'pending';
      }

      /*
       * Notify the rest of the theme about the cart update.
       */
      document.dispatchEvent(
        new DiscountUpdateEvent(
          data,
          discountComponent.id
        )
      );

      /*
       * Replace the live cart section with Shopify's
       * updated version.
       */
      morphSection(
        sectionId,
        newHtml
      );

      /*
       * Allow Horizon to finish the morph before checking
       * the newly-created DOM.
       */
      await this.#wait(100);

      const liveDialog = this.refs.dialog;

      const appliedPill =
        liveDialog?.querySelector(
          '.cart-discount__pill[data-discount-code="WELCOME10"]'
        );

      if (appliedPill) {
        const liveDiscountComponent =
          liveDialog?.querySelector(
            'cart-discount-component'
          );

        liveDiscountComponent?.setAttribute(
          'data-awadh-welcome10-applied',
          'true'
        );

        this.#dispatchWelcome10Applied();

        return 'applied';
      }

      /*
       * Sometimes the cart section can finish morphing
       * a fraction later on mobile.
       *
       * Return pending so the retry loop gets another chance.
       */
      return 'pending';
    } catch (error) {
      /*
       * AbortErrors and transient mobile network timing
       * should not permanently disable the feature.
       */
      console.error(
        'Awadh: failed to auto-apply WELCOME10.',
        error
      );

      return 'pending';
    } finally {
      this.#welcome10Applying = false;
    }
  };

  /**
   * Small Promise-based delay.
   *
   * @param {number} milliseconds
   * @returns {Promise<void>}
   */
  #wait = (milliseconds) =>
    new Promise((resolve) =>
      window.setTimeout(resolve, milliseconds)
    );

  /**
   * Signals that WELCOME10 has been successfully applied.
   *
   * Existing Fastrr / Shiprocket integration can listen to this
   * without us reimplementing checkout logic.
   */
  #dispatchWelcome10Applied = () => {
    document.dispatchEvent(
      new CustomEvent(
        'awadh:welcome10-applied',
        {
          bubbles: true,
          detail: {
            code: 'WELCOME10',
          },
        }
      )
    );
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked
     * to avoid overlapping dialogs.
     */
    customElements
      .whenDefined('shopify-payment-terms')
      .then(() => {
        const installmentsContent =
          document
            .querySelector(
              'shopify-payment-terms'
            )
            ?.shadowRoot;

        const cta =
          installmentsContent?.querySelector(
            '#shopify-installments-cta'
          );

        cta?.addEventListener(
          'click',
          this.closeDialog,
          {
            once: true,
          }
        );
      });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } =
      /** @type {Refs} */ (this.refs);

    if (!dialog) return;

    const content =
      dialog.querySelector(
        '.cart-drawer__content'
      );

    const summary =
      dialog.querySelector(
        '.cart-drawer__summary'
      );

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect()
        .height;

    const summaryHeight =
      summary.getBoundingClientRect()
        .height;

    const ratio =
      summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (
  !customElements.get(
    'cart-drawer-component'
  )
) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}